import { Component, OnDestroy, OnInit, isDevMode } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, Subject, take } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import {CentreService} from "../../../../../services/centre.service";
import {PermissionType} from "../../../../../components/employee/PermissionType.enum";
import {CounsellingPhotoGalleryComponent} from "../../../../counseling/counselling-photo-gallery/counselling-photo-gallery.component";
import { environment } from '../../../../../../environments/environment';
import {endOfDay} from '../../../../../utils/date-range-format.service';

@Component({
  selector: 'app-counselling-presentation',
  templateUrl: './counselling-presentation.component.html',
  styleUrls: ['./counselling-presentation.component.scss']
})
export class CounsellingPresentationComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
remarks=''
  PermissionType = PermissionType;

  // Environment for Firestore paths
  private env = isDevMode() ? environment.testMode : environment.productionMode;

  centres$!: Observable<any[]>;
  centresWithAll$!: Observable<any[]>;
  selectedCentres: string[] = ['All Centres'];
  // objects for searchable-select
  selectedCentreObjects: any[] = [];

  mStartDate!: Date;
  mEndDate!: Date;

  searchPressed: boolean = false;

  /**
   * Confirmed (locked) filter values — only updated when Search is clicked or an existing
   * presentation is loaded. Child components are bound to THESE, not to the live form fields
   * (mStartDate / mEndDate / selectedCentres), so they only re-fetch when Search is
   * explicitly pressed, not on every date-picker or centre-dropdown interaction.
   */
  confirmedCentres: string[] = [];
  confirmedStartDate: Date | null = null;
  confirmedEndDate: Date | null = null;


  // Store selected images from gallery
  selectedImagesForSave: any[] = [];

  // Store dashboard counsellor remarks (userName → remark)
  dashboardRemarks: { [userName: string]: string } = {};

  // Flag to prevent multiple save calls
  private isSaving: boolean = false;

  // Edit / View mode properties
  isEditMode: boolean = false;
  isViewMode: boolean = false;
  editDocumentId: string | null = null;
  isLoadingExistingData: boolean = false;

  components = [
    { id: 1, label: 'Dashboard', visible: true },
    { id: 2, label: 'Group Topics', visible: true },
    { id: 3, label: 'Highlights', visible: true },
    { id: 4, label: 'Gallery', visible: true },
    { id: 5, label: 'Remarks', visible: true },
    { id: 6, label: 'Assessment Charts', visible: true }
  ];
  selectedComponents: any[] = [];

  // Group Topics fetched from group sessions
  uniqueGroupTopics: string[] = [];
  isLoadingGroupTopics: boolean = false;

  toggleAllSelection() {
    // Use .every() instead of .length check — immune to ghost null/undefined values
    const allSelected = this.components.every(c => this.selectedComponents.includes(c.id));

    if (allSelected) {
      this.selectedComponents = [];        // deselect all
    } else {
      this.selectedComponents = this.components.map(c => c.id);  // select all
    }
  }

  onComponentsChange() {
    // Strip out any ghost null/undefined/selectAll values the mat-option pushes in
    this.selectedComponents = this.selectedComponents.filter(
      id => id != null && id !== 'selectAll'
    );
  }


  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private centreService: CentreService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private firestore: AngularFirestore,
    private auth: AngularFireAuth
  ) { }

  ngOnInit(): void {
    this.centres$ = this.centreService.getAllCentres().pipe(
      map(centres => [...centres].sort((a, b) => (a.centreName ?? '').trim().localeCompare((b.centreName ?? '').trim())))
    );
    this.centresWithAll$ = this.centres$.pipe();

    // Check if editing existing presentation
    this.activatedRoute.queryParams.pipe(takeUntil(this._destroyed$)).subscribe(params => {
      const docId = params['id'];
      const mode = params['mode'];

      if (docId) {
        console.log('Loading presentation for ID:', docId);
        this.editDocumentId = docId;
        this.isEditMode = mode === 'edit';
        this.isViewMode = mode !== 'edit';
        this.loadExistingPresentation(docId);
      } else {
        console.log('Creating new presentation');
        this.isEditMode = false;
        this.isViewMode = false;
        this.editDocumentId = null;
      }
    });
  }

  navigateToList() {
    this.router.navigate(['/home/reports/counselling-presentation-list']);
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  /**
   * Load existing presentation data from Firestore
   */
  async loadExistingPresentation(docId: string): Promise<void> {
    this.isLoadingExistingData = true;
    try {
      console.log('Fetching presentation data from Firestore...');
      const docSnapshot = await this.firestore.collection(this.env.counsellingPresentationList).doc(docId).get().toPromise();

      if (docSnapshot && docSnapshot.exists) {
        const data = docSnapshot.data() as any;
        console.log('Presentation data loaded:', data);

        // Populate form fields
        // Handle both old (string) and new (array) format
        if (Array.isArray(data.selectedCentres)) {
          this.selectedCentres = data.selectedCentres;
          // populate selectedCentreObjects for searchable-select
          this.populateSelectedCentreObjectsFromCodes(this.selectedCentres);
        } else if (data.selectedCentre) {
          this.selectedCentres = [data.selectedCentre];
          this.populateSelectedCentreObjectsFromCodes(this.selectedCentres);
        } else {
          this.selectedCentres = ['All Centres'];
        }
        this.mStartDate = data.startDate?.toDate() || new Date();
        this.mEndDate = data.endDate?.toDate() || new Date();
        this.selectedComponents = data.selectedComponents || [];
        this.remarks = data.remarks || '';
        this.selectedImagesForSave = data.galleryImages || [];
        this.dashboardRemarks = data.dashboardRemarks || {};

        // Mark search as pressed to enable save
        this.searchPressed = true;

        // Confirmed values — used by child components to avoid unnecessary re-fetching
        this.confirmedCentres = [...this.selectedCentres];
        this.confirmedStartDate = this.mStartDate;
        this.confirmedEndDate = this.mEndDate;

        // Load group topics for the existing presentation
        this.fetchGroupTopics();

        console.log('Form populated with existing data');
        console.log('Loaded data:', {
          centres: this.selectedCentres,
          startDate: this.mStartDate,
          endDate: this.mEndDate,
          components: this.selectedComponents,
          images: this.selectedImagesForSave.length
        });
      } else {
        console.error('Presentation not found');
        this.snackBar.open('Presentation not found', 'Close', { duration: 3000 });
        this.navigateToList();
      }
    } catch (error: any) {
      console.error('Error loading presentation:', error);
      this.snackBar.open(`Error loading presentation: ${error.message}`, 'Close', { duration: 5000 });
      this.navigateToList();
    } finally {
      this.isLoadingExistingData = false;
    }
  }

  onCentresSelected(selected: any) {
    if (!selected) return;

    // multiple selection: component returns array of selected items
    if (Array.isArray(selected)) {
      const values = selected.map((s: any) => (typeof s === 'string' ? s : s?.centreCode)).filter(Boolean).map(String);
      const last = values[values.length - 1];
      if (last === 'All Centres') {
        this.selectedCentres = ['All Centres'];
        this.selectedCentreObjects = [{ centreCode: 'All Centres', centreName: 'All Centres' }];
      } else if (values.includes('All Centres')) {
        this.selectedCentres = values.filter(v => v !== 'All Centres');
        this.populateSelectedCentreObjectsFromCodes(this.selectedCentres);
      } else {
        this.selectedCentres = values;
        this.selectedCentreObjects = selected;
      }
    } else {
      // single selection (not expected here) — map to value
      if (typeof selected === 'string' || typeof selected === 'number') {
        this.selectedCentres = [String(selected)];
        this.selectedCentreObjects = [{ centreCode: String(selected), centreName: '' }];
      } else {
        this.selectedCentres = [String(selected?.centreCode)];
        this.selectedCentreObjects = [selected];
      }
    }
  }

  private populateSelectedCentreObjectsFromCodes(codes: string[]) {
    if (!codes || codes.length === 0) {
      this.selectedCentreObjects = [];
      return;
    }
    this.centres$.pipe(take(1)).subscribe(arr => {
      this.selectedCentreObjects = codes.map(code => {
        if (code === 'All Centres') return { centreCode: 'All Centres', centreName: 'All Centres' };
        return arr?.find(c => c.centreCode === code) || { centreCode: code, centreName: code };
      });
    });
  }

  Search() {
    if (!this.selectedCentres || this.selectedCentres.length === 0 || !this.mStartDate || !this.mEndDate) {
      alert('Please select centre and date range');
      return;
    }

    // Create new Date objects and new array reference so Angular OnChanges fires in all child components
    this.mStartDate = new Date(this.mStartDate);
    this.mEndDate = new Date(this.mEndDate);
    this.selectedCentres = [...this.selectedCentres];

    this.searchPressed = true;

    // Set confirmed values — used by child components to avoid unnecessary re-fetching
    this.confirmedCentres = [...this.selectedCentres];
    this.confirmedStartDate = this.mStartDate;
    this.confirmedEndDate = this.mEndDate;

    // Fetch group topics for the selected centres and date range
    this.fetchGroupTopics();

    console.log('Search clicked', {
      centres: this.selectedCentres,
      startDate: this.mStartDate,
      endDate: this.mEndDate,
      selectedComponents: this.selectedComponents
    });
  }

  Clear() {
    this.selectedCentres = ['All Centres'];
    this.mStartDate = undefined as any;
    this.mEndDate = undefined as any;
    this.searchPressed = false;
    this.remarks = '';
    this.selectedComponents = [];
    this.selectedImagesForSave = [];
    this.dashboardRemarks = {};

    // Reset confirmed values
    this.confirmedCentres = [];
    this.confirmedStartDate = null;
    this.confirmedEndDate = null;
  }


  async Save() {
    // Prevent multiple simultaneous saves
    if (this.isSaving) {
      console.warn('Save already in progress, ignoring duplicate call');
      return;
    }

    console.log('Save method called');

    // Validation 1: Check if search was pressed
    if (!this.searchPressed) {
      console.warn('Search not pressed yet');
      this.snackBar.open('Please search first before saving', 'Close', { duration: 3000 });
      return;
    }

    // Validation 2: Check required fields
    if (!this.mStartDate || !this.mEndDate || !this.selectedCentres || this.selectedCentres.length === 0) {
      console.warn('Missing required fields:', {
        startDate: this.mStartDate,
        endDate: this.mEndDate,
        centres: this.selectedCentres
      });
      this.snackBar.open('Please select centre and date range', 'Close', { duration: 3000 });
      return;
    }

    // Validation 3: Check if at least one component is selected
    if (!this.selectedComponents || this.selectedComponents.length === 0) {
      console.warn('No sections selected');
      this.snackBar.open('Please select at least one section to save', 'Close', { duration: 3000 });
      return;
    }

    console.log('All validations passed. Proceeding to save...');
    console.log('Save data:', {
      centres: this.selectedCentres,
      startDate: this.mStartDate,
      endDate: this.mEndDate,
      selectedComponents: this.selectedComponents,
      galleryImages: this.selectedImagesForSave.length,
      remarks: this.remarks
    });

    // Set saving flag
    this.isSaving = true;

    try {
      // Get current user
      console.log('👤 Getting current user...');
      const currentUser = await this.auth.currentUser;
      const userName = currentUser?.email || 'Unknown User';
      console.log('User:', userName);

      // Get centre name - use a simpler approach to avoid toPromise() issues
      console.log('Getting centre names...');
      let centreNames: string[] = ['All Centres'];
      const isAllCentres = this.selectedCentres.includes('All Centres') || this.selectedCentres.length === 0;
      if (!isAllCentres) {
        try {
          // Use a timeout to prevent hanging
          const centresPromise = new Promise<any[]>((resolve, reject) => {
            const subscription = this.centres$.subscribe(
              centres => {
                subscription.unsubscribe();
                resolve(centres);
              },
              error => {
                subscription.unsubscribe();
                reject(error);
              }
            );

            // Timeout after 5 seconds
            setTimeout(() => {
              subscription.unsubscribe();
              reject(new Error('Timeout loading centres'));
            }, 5000);
          });

          const centres = await centresPromise;
          centreNames = this.selectedCentres.map(code => {
            const centre = centres?.find(c => c.centreCode === code);
            return centre?.centreName || code;
          });
          console.log('Centre names:', centreNames);
        } catch (centreError) {
          console.warn('Failed to load centre names, using codes:', centreError);
          centreNames = this.selectedCentres;
        }
      }

      // Prepare presentation data
      console.log('Preparing presentation data...');
      const presentationData = {
        selectedCentres: this.selectedCentres,
        selectedCentre: this.selectedCentres.includes('All Centres') ? 'All Centres' : this.selectedCentres.join(', '), // backward compat
        centreNames: centreNames,
        centreName: centreNames.join(', '),
        startDate: firebase.firestore.Timestamp.fromDate(this.mStartDate),
        endDate: firebase.firestore.Timestamp.fromDate(this.mEndDate),
        selectedComponents: this.selectedComponents,
        componentLabels: this.selectedComponents.map(id => {
          const comp = this.components.find(c => c.id === id);
          return comp?.label || '';
        }),
        remarks: this.remarks || '',

        // Gallery images
        galleryImages: this.selectedImagesForSave.map(img => ({
          url: img.url,
          fileName: img.fileName,
          description: img.description || '',
          centreName: img.centreName || '',
          centreCode: img.centreCode || '',
          sessionId: img.sessionId || '',
          sessionDate: img.sessionDate || null
        })),
        galleryImagesCount: this.selectedImagesForSave.length,

        // Dashboard counsellor remarks
        dashboardRemarks: this.dashboardRemarks || {},

        // Metadata
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        createdBy: userName,
        lastModified: firebase.firestore.FieldValue.serverTimestamp()
      };

      console.log('Presentation data prepared:', presentationData);

      // Save to Firestore - either update existing or create new
      if (this.isEditMode && this.editDocumentId) {
        // UPDATE mode — remove createdAt/createdBy so originals are preserved
        const { createdAt, createdBy, ...updateFields } = presentationData;
        const updateData = {
          ...updateFields,
          lastModified: firebase.firestore.FieldValue.serverTimestamp()
        };
        await this.firestore.collection(this.env.counsellingPresentationList).doc(this.editDocumentId).update(updateData);
        console.log('Updated successfully! Document ID:', this.editDocumentId);
        this.snackBar.open('Counselling Presentation updated successfully!', 'Close', { duration: 3000 });
      } else {
        // CREATE mode
        console.log('Saving to Firestore collection: ALCounsellingPresentationList');
        const docRef = await this.firestore.collection(this.env.counsellingPresentationList).add(presentationData);
        console.log('Saved successfully! Document ID:', docRef.id);
        this.snackBar.open('Counselling Presentation saved successfully!', 'Close', { duration: 3000 });
      }

      // Navigate to list
      console.log('Navigating to list in 1.5 seconds...');
      setTimeout(() => {
        this.navigateToList();
      }, 1500);

    } catch (error: any) {
      console.error('Error saving presentation:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      this.snackBar.open(`Error: ${error.message || 'Failed to save presentation'}`, 'Close', { duration: 5000 });
    } finally {
      // Reset saving flag
      this.isSaving = false;
      console.log('🏁 Save process completed, flag reset');
    }
  }

  isComponentSelected(componentId: number): boolean {
    return this.selectedComponents.includes(componentId);
  }

  /** Receive updated remarks map from the Dashboard (mis-counselling2) child */
  onDashboardRemarksChange(remarks: { [userName: string]: string }): void {
    this.dashboardRemarks = remarks;
  }



  openPhotoGallery() {
    const dialogRef = this.dialog.open(CounsellingPhotoGalleryComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '90vh',
      panelClass: 'counselling-photo-gallery-dialog-container',
      data: {
        centreCode: this.selectedCentres.includes('All Centres') ? 'All' : this.selectedCentres[0],
        startDate: this.mStartDate,
        endDate: this.mEndDate,
        sessionType: 'individual' // or 'group' based on your requirements
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this._destroyed$)).subscribe((selectedDocuments: any[]) => {
      if (selectedDocuments && selectedDocuments.length > 0) {
        this.handleSelectedDocuments(selectedDocuments);
      }
    });
  }

  /**
   * Handle selected documents from photo gallery
   */
  handleSelectedDocuments(documents: any[]): void {
    console.log('Selected documents:', documents);

    // Store selected images
    this.selectedImagesForSave = documents;

    // Automatically add Gallery section (id=4) to selected components if images are selected
    if (documents.length > 0 && !this.selectedComponents.includes(4)) {
      this.selectedComponents = [...this.selectedComponents, 4];
    }

    // Show success message
    this.snackBar.open(
      `${documents.length} image(s) selected successfully. Gallery section added.`,
      'Close',
      { duration: 3000 }
    );
  }

  /**
   * Remove image from gallery
   */
  removeImageFromGallery(index: number): void {
    if (index >= 0 && index < this.selectedImagesForSave.length) {
      this.selectedImagesForSave.splice(index, 1);
      this.snackBar.open('Image removed from gallery', 'Close', { duration: 2000 });

      // If no images left, remove Gallery (id=4) from selected sections
      if (this.selectedImagesForSave.length === 0) {
        const galleryIndex = this.selectedComponents.indexOf(4);
        if (galleryIndex > -1) {
          this.selectedComponents = this.selectedComponents.filter(id => id !== 4);
        }
      }
    }
  }

  /**
   * Clear all images from gallery
   */
  clearGallery(): void {
    this.selectedImagesForSave = [];
    this.selectedComponents = this.selectedComponents.filter(id => id !== 4);
    this.snackBar.open('Gallery cleared', 'Close', { duration: 2000 });
  }

  /**
   * Open image in new tab
   */
  openImageInNewTab(url: string): void {
    window.open(url, '_blank');
  }


  /**
   * Fetch group sessions for the confirmed centres & date range,
   * extract `topicsCovered` arrays, and compute unique topic strings.
   */
  fetchGroupTopics(): void {
    if (!this.confirmedStartDate || !this.confirmedEndDate) return;

    this.isLoadingGroupTopics = true;
    this.uniqueGroupTopics = [];

    const startDate = new Date(this.confirmedStartDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(this.confirmedEndDate);
    endDate.setHours(23, 59, 59, 999);

    const groupSessionsPath = `${this.env.counseling}/allSessions/groupSessions`;

    // Query group sessions within the date range
    this.firestore.collection<any>(groupSessionsPath, ref =>
      ref.where('date', '>=', startDate)
         .where('date', '<=', endOfDay(endDate))
         .orderBy('date', 'desc')
    ).get().pipe(takeUntil(this._destroyed$)).subscribe({
      next: (snapshot) => {
        let sessions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Filter by selected centres (if not "All Centres")
        const isAllCentres = this.confirmedCentres.includes('All Centres') || this.confirmedCentres.length === 0;
        if (!isAllCentres) {
          sessions = sessions.filter(session => {
            // Group sessions can have centreCode (string) or centres (array of objects)
            if (session.centreCode) {
              return this.confirmedCentres.includes(session.centreCode);
            }
            if (session.centres && Array.isArray(session.centres)) {
              return session.centres.some((c: any) => this.confirmedCentres.includes(c.centreCode));
            }
            return false;
          });
        }

        // Extract unique topics from topicsCovered arrays
        const topicsSet = new Set<string>();
        sessions.forEach(session => {
          if (session.topicsCovered && Array.isArray(session.topicsCovered)) {
            session.topicsCovered.forEach((topic: string) => {
              if (topic && topic.trim()) {
                topicsSet.add(topic.trim());
              }
            });
          }
        });

        this.uniqueGroupTopics = Array.from(topicsSet).sort();
        this.isLoadingGroupTopics = false;

        console.log('Group Topics fetched:', {
          totalSessions: sessions.length,
          uniqueTopics: this.uniqueGroupTopics.length,
          topics: this.uniqueGroupTopics
        });
      },
      error: (err) => {
        console.error('Error fetching group topics:', err);
        this.isLoadingGroupTopics = false;
      }
    });
  }

  getFirstSelectedLabel(): string {
    if (!this.selectedComponents || this.selectedComponents.length === 0) return '';
    const firstId = this.selectedComponents[0];
    const found = this.components.find(c => c.id === firstId);
    return found?.label || '';
  }

}
